import {
  createContext,
  type ReactNode,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { applyDensityAttr, isAppDensity } from "~/lib/density";

import { loadPreferences, savePreferences, type UserPreferences } from "./userPreferences";

interface UserPreferencesContextValue {
  preferences: UserPreferences;
  isDark: boolean;
  updatePreference: <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => void;
}

const PreferencesContext = createContext<UserPreferencesContextValue | null>(null);

export const UserPreferencesProvider = ({ children }: { children: ReactNode }) => {
  const [preferences, setPreferences] = useState(loadPreferences);
  const [systemDark, setSystemDark] = useState(
    () =>
      typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  const isDark = useMemo(() => {
    if (preferences.theme === "dark") {
      return true;
    }

    if (preferences.theme === "light") {
      return false;
    }

    return systemDark;
  }, [preferences.theme, systemDark]);

  const updatePreference = useCallback(
    <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) => {
      setPreferences((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  // Persist to localStorage whenever preferences change
  useEffect(() => {
    savePreferences(preferences);
  }, [preferences]);

  // Apply .dark class to <html> whenever isDark changes. Bracket the swap
  // with a `theme-switching` class for one paint frame so the CSS rule in
  // app.css can suppress transitions while CSS variables flip — eliminates
  // the brief "wrong-theme" tint flash on elements that have
  // `transition-colors` / `transition-all`.
  useEffect(() => {
    const root = document.documentElement;

    root.classList.add("theme-switching");
    root.classList.toggle("dark", isDark);

    // Two RAFs: first to ensure the class change is committed to the next
    // frame, second to remove the guard *after* the browser has painted
    // with new theme variables. One RAF alone sometimes lets the guard
    // come off before paint, which re-introduces the flash on slower hardware.
    const id1 = requestAnimationFrame(() => {
      // Stash for cleanup
      (root as HTMLElement & { __themeRaf2?: number }).__themeRaf2 = requestAnimationFrame(() => {
        root.classList.remove("theme-switching");
      });
    });

    return () => {
      cancelAnimationFrame(id1);

      const stash = (root as HTMLElement & { __themeRaf2?: number }).__themeRaf2;

      if (stash !== undefined) {
        cancelAnimationFrame(stash);
      }

      root.classList.remove("theme-switching");
    };
  }, [isDark]);

  // Apply density token axis on <html>.
  useEffect(() => {
    if (isAppDensity(preferences.density)) {
      applyDensityAttr(preferences.density);
    }
  }, [preferences.density]);

  // Listen for system theme changes
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);

    mq.addEventListener("change", handler);

    return () => mq.removeEventListener("change", handler);
  }, []);

  const value = useMemo(
    () => ({ preferences, isDark, updatePreference }),
    [preferences, isDark, updatePreference],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const useUserPreferences = (): UserPreferencesContextValue => {
  const ctx = use(PreferencesContext);

  if (!ctx) {
    throw new Error("useUserPreferences must be used within a UserPreferencesProvider");
  }

  return ctx;
};
